import { Component, Vue, Prop, Watch } from "vue-property-decorator";
import { getModule } from "vuex-module-decorators";
import { Spin, Table, Dropdown, Icon, Button, Menu, Drawer, Modal, Radio } from "ant-design-vue";
import { Column } from "ant-design-vue/lib";
import "ant-design-vue/dist/antd.less";
import { VersionGroupModel } from "@/services/version-group";
import { ILessonPlanVersionModel, LessonPlanVersionColumns } from "@/models/lesson-plan";
import LessonPlanVersionState from "@/modules/lesson-plan-version";
import { VersionGroupModule } from "@/modules/versiongroup";
import { LessonPlanLocale, CommonLocale } from "@/locales/localeid";
import { fmtMsg, GLUtil, ActionItem } from "commonui";
import {
	AccessLevel,
	LessonPlanVersionAction,
	LessonPlanVersionActionStatus,
	LessonPlanVersionStatus,
	MethodologyType,
	MethodologyTypeEnum
} from "@/util/enum";
import { LessonPlanVersionStatusMap, MethodologyTypeItemsWithAll } from "@/util/constants";
import PageTitle from "@/components/page-title/page-title.vue";
import VersionGroupSelect from "@/components/version-group-select/version-group-select.vue";
import MethodologySelect from "@/components/methodology-select/methodology-select.vue";
import DetailContent from "./components/detail/detail.vue";
import DetailContentType from "./components/detail/detail";
import { Paths } from "@/util/paths";
import LessonPlanListState from "@/modules/lesson-plan-list";
import { getRouteAccessLevel, disabledDueToAccessLevel } from "@/util/func";
import Grid from "@/components/wijmo-grid/grid/grid.vue";

@Component({
	components: {
		Grid,
		"a-spin": Spin,
		"a-table": Table,
		"a-dropdown": Dropdown,
		"a-icon": Icon,
		"a-button": Button,
		"a-menu": Menu,
		"a-menu-item": Menu.Item,
		"a-drawer": Drawer,
		PageTitle,
		VersionGroupSelect,
		MethodologySelect,
		DetailContent,
		ActionItem,
		Radio,
		RadioGroup: Radio.Group
	}
})
export default class Versions extends Vue {
	private lessonPlanListModule = getModule(LessonPlanListState);
	private lessonPlanVersionModule = getModule(LessonPlanVersionState);
	private versionGroupModule = VersionGroupModule; // for watch need to create local reference
	public versionStatusValue: LessonPlanVersionStatus = LessonPlanVersionStatus.Draft;
	public draftCount: number = 0;
	public publishedCount: number = 0;
	public internalCount: number = 0;
	public filteredlessonPlanVersions: ILessonPlanVersionModel[] = [];
	private displayVersionDetail: boolean = false;
	public currentAction: LessonPlanVersionActionStatus = LessonPlanVersionActionStatus.Edit;
	public accessLevel = AccessLevel;
	public isLPVSaving: boolean = false;
	public enableSaveButton: boolean = false;

	get loading(): boolean {
		return this.lessonPlanVersionModule.loading;
	}

	get lessonPlanLocale(): any {
		return LessonPlanLocale;
	}

	get commonLocale(): any {
		return CommonLocale;
	}

	get lessonPlanVersionAction(): any {
		return LessonPlanVersionAction;
	}

	get lessonPlanVersionActionStatus(): any {
		return LessonPlanVersionActionStatus;
	}

	get lessonPlanVersionStatus(): any {
		return LessonPlanVersionStatus;
	}

	get lessonPlanVersionStatusMap(): any {
		return LessonPlanVersionStatusMap;
	}

	get lessonPlanVersionColumns(): any {
		return LessonPlanVersionColumns;
	}

	get versionGroups(): VersionGroupModel[] {
		return this.lessonPlanVersionModule.currentVersionGroups;
	}

	get defaultVersionGroup(): string {
		return this.lessonPlanVersionModule.currentVersionGroup.id;
	}

	get defaultMethodology(): number {
		return this.lessonPlanVersionModule.currentMethodology;
	}

	get methodologyItems(): Map<MethodologyTypeEnum, string> {
		return MethodologyTypeItemsWithAll;
	}

	get lessonPlanVersions(): ILessonPlanVersionModel[] {
		return this.lessonPlanVersionModule.lessonPlanVersions;
	}

	get currentVersionGroups(): VersionGroupModel[] {
		return this.lessonPlanVersionModule.currentVersionGroups;
	}

	get currentVersionGroup(): VersionGroupModel {
		return this.lessonPlanVersionModule.currentVersionGroup;
	}

	get currentLessonPlanVersion(): ILessonPlanVersionModel {
		return this.lessonPlanVersionModule.currentLessonPlanVersion;
	}

	get lessonPlanVersionFormTitle(): string {
		switch (this.currentAction) {
			case LessonPlanVersionActionStatus.Edit:
				return fmtMsg(LessonPlanLocale.LessonPlanVersionEditFormTitle);
			case LessonPlanVersionActionStatus.Duplicate:
				return fmtMsg(LessonPlanLocale.LessonPlanVersionDuplicateFormTitle);
			case LessonPlanVersionActionStatus.Add:
			default:
				return fmtMsg(LessonPlanLocale.LessonPlanVersionAddFormTitle);
		}
	}

	get currentRouteAccessLevel() {
		return getRouteAccessLevel(this.$route);
	}

	get disabledDueToAccessLevel() {
		return disabledDueToAccessLevel(this.currentRouteAccessLevel);
	}

	onVersionStatusChange(e: any) {
		this.setFilteredData(this.lessonPlanVersions, parseInt(e.target.value));
	}

	mounted(): void {
		this.versionGroupModule.setDisabled(false);
	}

	toggleSave(toggleState: boolean): void {
		this.enableSaveButton = toggleState;
	}

	destroyed(): void {
		this.versionGroupModule.setDisabled(true);
	}

	formatMessage(id: string, params?: any): string {
		return fmtMsg(id, params);
	}

	getLessonPlanListRouter(record: ILessonPlanVersionModel): string {
		return GLUtil.pathStringify(Paths.LessonPlanListWithParams, {
			versionGroupId: record.versionGroupId,
			methodology: `${record.methodology}`,
			lessonPlanVersionId: record.id
		});
	}

	localizedString(id: string, params?: {}) {
		return fmtMsg(id, params);
	}

	renderState(text: string, cnt: number) {
		const localizedValue = this.localizedString(text);
		const countValue = cnt || 0;
		return `<span>${localizedValue}</span>
                <span class="cnt">(${countValue})</span>`;
	}

	doAction(event: any, record: VersionGroupModel): void {
		switch (event.key) {
			case LessonPlanVersionAction.Edit:
				this.onEditLessonPlanVersion(record.id);
				break;
			case LessonPlanVersionAction.Publish:
				this.onPublishLessonPlanVersion(record);
				break;
			case LessonPlanVersionAction.Remove:
				this.onDeleteLessonPlanVersion(record.id, record);
				break;
			case LessonPlanVersionAction.Duplicate:
				this.onDuplicateLessonPlanVersion(record.id);
				break;
			case LessonPlanVersionAction.Internal:
				this.onInternalLessonPlanVersion(record);
				break;
			case LessonPlanVersionAction.Draft:
				this.onMovingToDraft(record);
				break;
			default:
				break;
		}
	}

	@Watch("versionGroupModule.selectedGsVersion")
	@Watch("versionGroupModule.selectedLsVersion")
	onVersionGroupChanged(value: VersionGroupModel): void {
		// this.lessonPlanVersionModule.setVersionGroup(value);
		this.lessonPlanVersionModule.refreshLessonPlanVersions();
	}

	@Watch("lessonPlanVersionModule.lessonPlanVersions", { immediate: true, deep: true })
	onLessonPlanVersionsDataChange(data: ILessonPlanVersionModel[]) {
		this.setFilteredData(data, this.versionStatusValue);
	}

	setFilteredData(data: ILessonPlanVersionModel[], status: LessonPlanVersionStatus) {
		this.filteredlessonPlanVersions = this.formatData(data.filter((d) => d.status === status));
		this.draftCount = data.filter((d) => d.status === LessonPlanVersionStatus.Draft).length;
		this.internalCount = data.filter((d) => d.status === LessonPlanVersionStatus.Internal).length;
		this.publishedCount = data.filter((d) => d.status === LessonPlanVersionStatus.Published).length;
	}

	formatData(data: ILessonPlanVersionModel[]) {
		return data.map((lessonPlanVersion) => ({
			...lessonPlanVersion,
			methodologyText: this.methodologyItems.get(lessonPlanVersion.methodology as MethodologyTypeEnum),
			statusText: this.lessonPlanVersionStatusMap.get(lessonPlanVersion.status)
		}));
	}

	onMethodologyChanged(value: MethodologyTypeEnum): void {
		this.lessonPlanVersionModule.setMethodology(value);
		this.lessonPlanVersionModule.refreshLessonPlanVersions();
	}

	onNavigate2LessonPlanList(record: ILessonPlanVersionModel): void {
		this.$router.push({
			path: GLUtil.pathStringify(Paths.LessonPlanListWithParams, {
				versionGroupId: record.versionGroupId,
				methodology: `${record.methodology}`,
				lessonPlanVersionId: record.id
			})
		});
	}

	onCloseVersionDetail(): void {
		this.displayVersionDetail = false;
		(this.$refs.detailContent as DetailContentType).onResetFormFieldsValue();
	}

	onAddLessonPlanVersion(event: any): void {
		const currentMethodology: MethodologyTypeEnum | MethodologyType = this.lessonPlanVersionModule
			.currentMethodology;
		this.lessonPlanVersionModule.setCurrentLessonPlanVersion({
			versionGroupId: this.lessonPlanVersionModule.currentVersionGroup.id,
			methodology: currentMethodology === MethodologyTypeEnum.All ? MethodologyType.Classic : currentMethodology
		});
		this.currentAction = LessonPlanVersionActionStatus.Add;
		this.displayVersionDetail = true;
	}

	onEditLessonPlanVersion(versionId: string): void {
		this.lessonPlanVersionModule.fetchLessonPlanVersion({ versionId, action: LessonPlanVersionActionStatus.Edit });
		this.currentAction = LessonPlanVersionActionStatus.Edit;
		this.displayVersionDetail = true;
	}

	onDuplicateLessonPlanVersion(versionId: string): void {
		this.lessonPlanVersionModule.fetchLessonPlanVersion({
			versionId,
			action: LessonPlanVersionActionStatus.Duplicate
		});
		this.currentAction = LessonPlanVersionActionStatus.Duplicate;
		this.displayVersionDetail = true;
	}

	onSaveLessonPlanVersion(): void {
		this.isLPVSaving = true;
		const success: (values: any) => Promise<void> = async (values) => {
			Object.keys(values).forEach((valueKey) => {
				this.lessonPlanVersionModule.currentLessonPlanVersion[valueKey] = values[valueKey];
			});
			const result: boolean = await this.lessonPlanVersionModule.saveLessonPlanVersionData(this.currentAction);
			if (result) {
				this.onCloseVersionDetail();
			}
			this.isLPVSaving = false;
		};
		const error: (errors: any, values: any) => void = (errors, values) => {
			// console.log(errors);
			this.isLPVSaving = false;
		};
		(this.$refs.detailContent as DetailContentType).onSubmitDetailForm(success, error);
	}

	async onInternalLessonPlanVersion(record: ILessonPlanVersionModel): Promise<void> {
		const confirmMessage: string = LessonPlanLocale.LessonPlanVersionSetInternalPromptMessage;
		if (record.status === LessonPlanVersionStatus.Draft) {
			if (record.methodology == MethodologyType.REP) {
				await this.lessonPlanVersionModule.IsTenLessonsForDiffUnitsREP(record.id!);
			}
			if (!this.lessonPlanVersionModule.canPublishedForREP) {
				Modal.warning({
					title: fmtMsg(LessonPlanLocale.LessonPlanVersionPublishREPPromptMessage)
				});
				return;
			}
		}
		this.showModalAndChangeLpStatus(confirmMessage, LessonPlanVersionStatus.Internal, record);
	}

	async onMovingToDraft(record: ILessonPlanVersionModel): Promise<void> {
		const confirmMessage: string =
			record.status === LessonPlanVersionStatus.Published
				? LessonPlanLocale.LessonPlanVersionUnpublishPromptMessage
				: LessonPlanLocale.LessonPlanVersionInternalRevertPromptMessage;

		this.showModalAndChangeLpStatus(confirmMessage, LessonPlanVersionStatus.Draft, record);
	}

	async onPublishLessonPlanVersion(record: ILessonPlanVersionModel): Promise<void> {
		const confirmMessage: string = LessonPlanLocale.LessonPlanVersionPublishPromptMessage;
		if (record.status === LessonPlanVersionStatus.Draft) {
			if (record.methodology == MethodologyType.REP) {
				await this.lessonPlanVersionModule.IsTenLessonsForDiffUnitsREP(record.id!);
			}
			if (!this.lessonPlanVersionModule.canPublishedForREP) {
				Modal.warning({
					title: fmtMsg(LessonPlanLocale.LessonPlanVersionPublishREPPromptMessage)
				});
				return;
			}
		}

		this.showModalAndChangeLpStatus(confirmMessage, LessonPlanVersionStatus.Published, record);
	}

	private showModalAndChangeLpStatus(
		message: string,
		status: LessonPlanVersionStatus,
		record: ILessonPlanVersionModel
	) {
		Modal.confirm({
			title: fmtMsg(message),
			okText: fmtMsg(CommonLocale.CommonYesButtonText),
			okType: "danger",
			cancelText: fmtMsg(CommonLocale.CommonNoButtonText),
			onOk: () => {
				this.lessonPlanVersionModule.changeLessonPlanVersionStatus({
					lessonPlanVersion: record,
					newStatus: status
				});
			}
		});
	}

	onDeleteLessonPlanVersion(versionId: string, record: ILessonPlanVersionModel): void {
		Modal.confirm({
			title: fmtMsg(LessonPlanLocale.LessonPlanVersionDeletePromptMessage),
			okText: fmtMsg(CommonLocale.CommonYesButtonText),
			okType: "danger",
			cancelText: fmtMsg(CommonLocale.CommonNoButtonText),
			onOk: () => {
				this.lessonPlanVersionModule.deleteLessonPlanVersion({
					lessonPlanVersion: record,
					versionId: versionId
				});
			}
		});
	}
}
